'use client'

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  useSyncExternalStore,
  type ReactNode,
} from 'react'
import { Direction } from 'radix-ui'
import { ENGLISH_MESSAGES, loadMessages } from './catalogues'
import { directionFor } from './direction'
import { persistLocale, readStoredLocale, toSupportedLocale } from './locale-preference'
import { defaultNumberLocale, numbersDifferFromEnglish } from './number-region'
import { translate } from './translate'
import type { I18nContextValue, Locale, Messages } from './types'

/**
 * Outside a provider (an isolated root, a portal mounted elsewhere, a spec)
 * t() answers in English, exactly as the provider's English fallback rung
 * does. It used to echo the key, which put "play.library.stats.roundsPlayed"
 * on a button. A key no catalogue holds is still returned as it is. Numbers
 * here are plain English: nothing outside a provider re-renders after mount,
 * so there is no safe moment to apply the browser's region.
 */
const I18nContext = createContext<I18nContextValue>({
  locale: 'en',
  setLocale: () => {},
  t: (key, params) => translate(ENGLISH_MESSAGES, ENGLISH_MESSAGES, key, params),
})

interface Active {
  locale: Locale
  messages: Messages
}

// "Is this the browser, after hydration?" without a setState in an effect.
const noopSubscribe = () => () => {}
const isClient = () => true
const isServer = () => false

const ENGLISH: Active = { locale: 'en', messages: ENGLISH_MESSAGES }

/** <html lang dir> follow the language actually on screen. */
function applyDocumentLocale(locale: Locale) {
  if (typeof document === 'undefined') return
  const root = document.documentElement
  root.setAttribute('lang', locale)
  root.setAttribute('dir', directionFor(locale))
}

/**
 * The interface language.
 *
 * - The first render is always English, matching the server render, so
 *   hydration never disagrees; the saved choice is applied right after mount.
 *   The inline <head> script (locale-bootstrap.tsx) has already set lang/dir
 *   by then, so the page does not paint left-to-right first.
 * - The language and its catalogue change together, once the catalogue has
 *   loaded; a slower, older load never overwrites a newer choice.
 * - <html dir> is set from the language (it used to be removed), and Radix
 *   primitives get the same direction through its DirectionProvider.
 * - Only catalogued languages are applied or saved.
 * - An English page groups catalogue numbers like its own toLocaleString()
 *   renders (en-IN or hi-IN: 1,38,000): in the browser's default formatting
 *   locale, in Latin digits. Only in the browser, after hydration, so the
 *   server render and the render that hydrates it agree.
 */
export function I18nProvider({ children }: { children: ReactNode }) {
  const [active, setActive] = useState<Active>(ENGLISH)
  const current = useRef<Active>(ENGLISH)
  const latestRequest = useRef(0)
  // The server does not format like the browser, so the server render (and
  // the client render that hydrates it) is region-free; React re-renders
  // right after hydration with the browser's snapshot. A locale that writes
  // numbers like plain English (en-US, en-GB, ar-AE) is not borrowed, so `t`
  // keeps its identity and nothing re-renders for it.
  const hydrated = useSyncExternalStore(noopSubscribe, isClient, isServer)
  const englishRegion = useMemo(() => {
    if (!hydrated) return undefined
    const locale = defaultNumberLocale()
    return locale && numbersDifferFromEnglish(locale) ? locale : undefined
  }, [hydrated])

  const activate = useCallback(async (locale: Locale) => {
    const request = ++latestRequest.current
    persistLocale(locale)
    if (locale === current.current.locale) {
      applyDocumentLocale(locale)
      return
    }
    try {
      const messages = await loadMessages(locale)
      if (request !== latestRequest.current) return
      const next = { locale, messages }
      current.current = next
      applyDocumentLocale(locale)
      setActive(next)
    } catch (error) {
      if (request !== latestRequest.current) return
      // Stay in the language on screen, and put the document back in step
      // with it (the <head> script may have set a direction for the saved
      // choice). The saved choice is kept: a failed fetch is usually transient.
      applyDocumentLocale(current.current.locale)
      console.warn(`[i18n] could not load the "${locale}" catalogue`, error)
    }
  }, [])

  useEffect(() => {
    const saved = readStoredLocale()
    if (saved) void activate(saved)
  }, [activate])

  const setLocale = useCallback(
    (requested: Locale) => {
      const locale = toSupportedLocale(requested)
      if (locale) void activate(locale)
    },
    [activate],
  )

  // Only an English page borrows the page's grouping; other languages keep
  // their own (Hindi already groups 1,38,000).
  const numberLocale = active.locale === 'en' ? englishRegion : undefined

  const t = useCallback(
    (key: string, params?: Record<string, string | number>): string =>
      // English is the fallback rung, not the key path — a string added to
      // en.json but not yet back-ported must not surface as literal
      // "common.undo" to an Arabic reader. See lib/i18n/translate.ts.
      translate(active.messages, ENGLISH_MESSAGES, key, params, { locale: active.locale, numberLocale }),
    [active, numberLocale],
  )

  const value = useMemo<I18nContextValue>(() => ({ locale: active.locale, setLocale, t }), [active.locale, setLocale, t])

  return (
    <I18nContext.Provider value={value}>
      <Direction.Provider dir={directionFor(active.locale)}>{children}</Direction.Provider>
    </I18nContext.Provider>
  )
}

export function useTranslation(): I18nContextValue {
  return useContext(I18nContext)
}
